import clsx from 'clsx';
import { Download, Eye, FileImage, FileText, Paperclip, Trash2, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { base64ToBytes, saveFile } from '../lib/download';
import { dt } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { Button, ConfirmModal, ErrorBox, IconButton, Loading, Modal, Notice, useToast } from './ui';

/** Types accepted for documents of absences (the server checks the content as well). */
export const DOCUMENT_ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif';
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;
export const MAX_DOCUMENTS = 5;

const EXT_MIME: Record<string, string> = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif' };

export interface PickedFile {
  name: string;
  mime: string;
  size: number;
  base64: string;
}

/** Reads a file for the upload; null with a reason when type or size do not fit. */
export async function readDocument(file: File): Promise<{ ok: PickedFile } | { error: 'type' | 'size' }> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const mime = Object.values(EXT_MIME).includes(file.type) ? file.type : EXT_MIME[ext];
  if (!mime) return { error: 'type' };
  if (file.size > MAX_DOCUMENT_BYTES || file.size === 0) return { error: 'size' };
  const base64 = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
  return { ok: { name: file.name, mime, size: file.size, base64 } };
}

export const sizeText = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/** Text lines of a simple PDF (uncompressed text operators), for the preview where the browser cannot show PDFs. */
function pdfTextLines(bytes: Uint8Array): string[] | null {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  if (/\/Filter\s*\/FlateDecode/.test(s)) return null;
  const out: string[] = [];
  const re = /\(((?:\\.|[^\\)])*)\)\s*Tj/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) out.push(m[1].replace(/\\([\\()])/g, '$1'));
  return out.length ? out : null;
}

/** File picker with the chosen files (before they are uploaded, e.g. in the request dialog). */
export function FilePickList({ files, onChange, hint }: { files: PickedFile[]; onChange: (f: PickedFile[]) => void; hint?: ReactNode }) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);
  const add = async (list: FileList | null) => {
    if (!list?.length) return;
    setError(null);
    const next = [...files];
    for (const f of [...list]) {
      if (next.length >= MAX_DOCUMENTS) {
        setError(t('documents.tooMany', { count: MAX_DOCUMENTS }));
        break;
      }
      const r = await readDocument(f);
      if ('error' in r) setError(t(r.error === 'type' ? 'documents.badType' : 'documents.tooLarge', { name: f.name }));
      else next.push(r.ok);
    }
    onChange(next);
  };
  return (
    <div className="flex flex-col gap-2">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          void add(e.dataTransfer.files);
        }}
        className={clsx('flex flex-wrap items-center gap-3 rounded-md border border-dashed px-3 py-2.5 text-sm', drag ? 'border-accent bg-accent-soft' : 'border-line-strong')}
      >
        <Button size="sm" icon={<Paperclip className="size-3.5" />} onClick={() => input.current?.click()} disabled={files.length >= MAX_DOCUMENTS}>
          {t('documents.choose')}
        </Button>
        <span className="text-xs text-graphite">{hint ?? t('documents.pickHint')}</span>
        <input
          ref={input}
          type="file"
          accept={DOCUMENT_ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => {
            void add(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {files.length > 0 && (
        <ul className="flex flex-col gap-1">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded border border-line px-2 py-1 text-sm">
              {f.mime === 'application/pdf' ? <FileText className="size-4 text-graphite" /> : <FileImage className="size-4 text-graphite" />}
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              <span className="num text-xs text-graphite">{sizeText(f.size)}</span>
              <IconButton label={t('common.delete')} icon={<Trash2 className="size-3.5" />} onClick={() => onChange(files.filter((_, j) => j !== i))} />
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-xs text-bad">{error}</p>}
    </div>
  );
}

/** Shows a document of an absence: pictures inline, PDF in the browser viewer (in the demo as text preview). */
export function DocumentViewer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const open = useM('leave.document');
  const [file, setFile] = useState<{ fileName: string; mime: string; base64: string } | null>(null);
  useEffect(() => {
    setFile(null);
    if (id) open.mutateAsync({ id }).then(setFile).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const url = useMemo(() => {
    if (!file || file.mime !== 'application/pdf' || __DEMO__) return null;
    return URL.createObjectURL(new Blob([base64ToBytes(file.base64) as BlobPart], { type: file.mime }));
  }, [file]);
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);
  const lines = useMemo(() => (file && file.mime === 'application/pdf' && __DEMO__ ? pdfTextLines(base64ToBytes(file.base64)) : null), [file]);
  return (
    <Modal
      open={!!id}
      onClose={onClose}
      wide
      title={file?.fileName ?? t('documents.title')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.close')}</Button>
          {file && (
            <Button variant="primary" icon={<Download className="size-4" />} onClick={() => saveFile({ fileName: file.fileName, mime: file.mime, base64: file.base64 })}>
              {t('documents.download')}
            </Button>
          )}
        </>
      }
    >
      {open.isPending && <Loading />}
      <ErrorBox error={open.error} />
      {file && file.mime.startsWith('image/') && file.mime !== 'image/heic' && file.mime !== 'image/heif' && (
        <img src={`data:${file.mime};base64,${file.base64}`} alt={file.fileName} className="mx-auto max-h-[70vh] rounded border border-line" />
      )}
      {file && (file.mime === 'image/heic' || file.mime === 'image/heif') && <Notice tone="neutral">{t('documents.heicHint')}</Notice>}
      {url && <iframe src={url} title={file?.fileName} className="h-[70vh] w-full rounded border border-line bg-white" />}
      {file && file.mime === 'application/pdf' && __DEMO__ && (
        <div className="flex flex-col gap-3">
          <Notice tone="warn">{t('documents.demoPdf')}</Notice>
          {lines ? (
            <div className="mx-auto w-full max-w-[560px] rounded border border-line bg-white p-8 text-[#17212b] shadow-sm">
              <div className="mb-6 text-lg font-semibold">{lines[0]}</div>
              {lines.slice(1).map((l, i) => (
                <p key={i} className="mb-2 text-sm">
                  {l}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-sm text-graphite">{t('documents.noPreview')}</p>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * Documents of one absence: list, open, add (person while open or approved, HR always) and remove (person while the
 * request is open; HR with a reason). Opening a document of somebody else is recorded.
 */
export function AbsenceDocuments({ absenceId, expected, compact }: { absenceId: string; expected?: boolean; compact?: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const q = useQ('leave.documents', { absenceId });
  const invalidate = ['leave.', 'absences.', 'time.badges'];
  const add = useM('leave.addDocument', { invalidate });
  const remove = useM('leave.removeDocument', { invalidate });
  const [view, setView] = useState<string | null>(null);
  const [del, setDel] = useState<{ id: string } | null>(null);
  const [picked, setPicked] = useState<PickedFile[]>([]);
  const list = q.data?.attachments ?? [];
  const upload = async () => {
    for (const f of picked) await add.mutateAsync({ absenceId, fileName: f.name, mime: f.mime, base64: f.base64 });
    setPicked([]);
    toast.ok(t('documents.added'));
  };
  return (
    <div className="flex flex-col gap-3">
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && list.length === 0 && <p className={clsx('text-sm', expected ? 'text-warn' : 'text-graphite')}>{expected ? t('documents.missing') : t('documents.none')}</p>}
      {list.length > 0 && (
        <ul className="flex flex-col gap-1">
          {list.map((d) => (
            <li key={d.id} className="flex items-center gap-2 rounded border border-line px-2 py-1.5 text-sm">
              {d.mime === 'application/pdf' ? <FileText className="size-4 shrink-0 text-graphite" /> : <FileImage className="size-4 shrink-0 text-graphite" />}
              <button type="button" className="min-w-0 flex-1 truncate text-left text-accent hover:underline" onClick={() => setView(d.id)}>
                {d.fileName}
              </button>
              {!compact && (
                <span className="hidden text-xs text-graphite sm:inline">
                  {sizeText(d.size)} · {dt(d.uploadedAt)}
                  {d.uploadedBy ? ` · ${d.uploadedBy}` : ''}
                </span>
              )}
              <IconButton label={t('documents.view')} icon={<Eye className="size-3.5" />} onClick={() => setView(d.id)} />
              {d.canDelete && <IconButton label={t('common.delete')} icon={<Trash2 className="size-3.5" />} onClick={() => setDel({ id: d.id })} />}
            </li>
          ))}
        </ul>
      )}
      {q.data?.canAdd && list.length < MAX_DOCUMENTS && (
        <div className="flex flex-col gap-2">
          <FilePickList files={picked} onChange={setPicked} />
          {picked.length > 0 && (
            <Button size="sm" variant="primary" icon={<Upload className="size-3.5" />} className="self-start" loading={add.isPending} onClick={() => upload().catch(toast.error)}>
              {t('documents.upload', { count: picked.length })}
            </Button>
          )}
        </div>
      )}
      <DocumentViewer id={view} onClose={() => setView(null)} />
      <ConfirmModal
        open={!!del}
        onClose={() => setDel(null)}
        title={t('documents.removeTitle')}
        text={t('documents.removeText')}
        confirmLabel={t('common.delete')}
        danger
        reason="optional"
        loading={remove.isPending}
        error={remove.error}
        onConfirm={(reason) =>
          remove
            .mutateAsync({ id: del!.id, reason: reason || null })
            .then(() => {
              setDel(null);
              toast.ok(t('documents.removed'));
            })
            .catch(() => {})
        }
      />
    </div>
  );
}

/** Button with the number of documents that opens the documents of an absence in a dialog. */
export function DocumentsButton({ absenceId, count, expected, title }: { absenceId: string; count: number; expected?: boolean; title: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const missing = expected && count === 0;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={clsx(
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
          missing ? 'border-warn/40 bg-warn-soft text-warn' : count ? 'border-accent/30 bg-accent-soft text-accent' : 'border-line text-graphite hover:bg-sheet-2',
        )}
        title={missing ? t('documents.missing') : t('documents.title')}
      >
        <Paperclip className="size-3" aria-hidden />
        {missing ? t('documents.missingShort') : count ? t('documents.count', { count }) : t('documents.add')}
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title={title}>
        <AbsenceDocuments absenceId={absenceId} expected={expected} />
      </Modal>
    </>
  );
}
